import mapboxgl from 'mapbox-gl';

export const MAP_STYLES = {
  SATELLITE: {
    url: 'mapbox://styles/buckrivard/clertmasg000t01mvw0cvb340',
    name: 'Satellite',
  },
  STREET: { url: 'mapbox://styles/buckrivard/clerial3b000m01mv5fntb5r1', name: 'Street' },
};

export const MAP_PIN_STATUS_FILTERS = {
  ACTIVE: 'active',
  SOLD: 'sold',
  PENDING: 'pending',
  'SELECTED COMPS': 'selected comps',
  OTHER: 'other',
} as const;

export type PinStatusFilter = (typeof MAP_PIN_STATUS_FILTERS)[keyof typeof MAP_PIN_STATUS_FILTERS];

export const DEFAULT_SELECTED_STATUSES: PinStatusFilter[] = [
  MAP_PIN_STATUS_FILTERS['SELECTED COMPS'],
  MAP_PIN_STATUS_FILTERS.ACTIVE,
  MAP_PIN_STATUS_FILTERS.SOLD,
  MAP_PIN_STATUS_FILTERS.PENDING,
];

export const INITIAL_STYLE = MAP_STYLES.SATELLITE;

export const MAP_SOURCES = Object.freeze({
  SUBJECT: 'subject',
  PINS: 'pins',
});

export const MAP_LAYERS = Object.freeze({
  SUBJECT: 'subject',
  PINS: 'pins',
});

export const MAP_LAYER_DEFINITIONS: {
  SUBJECT: mapboxgl.AnyLayer;
  PINS: mapboxgl.AnyLayer;
} = Object.freeze({
  PINS: {
    id: MAP_LAYERS.PINS,
    type: 'symbol',
    source: MAP_SOURCES.PINS,
    layout: {
      'icon-image': ['get', 'icon'],
      'icon-anchor': 'center',
      'icon-allow-overlap': true,
      'icon-size': 1,
      'text-field': ['get', 'label'],
      'text-anchor': 'center',
      'text-offset': [0, -0.5],
      'text-size': 14,
      'text-allow-overlap': true,
    },
    paint: {
      'text-color': [
        'case',
        ['boolean', ['get', 'selected'], false],
        '#fff',
        ['boolean', ['get', 'active'], false],
        '#fff',
        '#000',
      ],
    },
  },
  SUBJECT: {
    id: MAP_LAYERS.SUBJECT,
    type: 'symbol',
    source: MAP_SOURCES.SUBJECT,
    layout: {
      'icon-image': 'Subject',
      'icon-size': 1.5,
      'icon-allow-overlap': false,
    },
  },
});

export const DRAW_MAPBOX_STYLES = [
  {
    id: 'gl-draw-polygon-fill',
    type: 'fill',
    filter: ['all', ['==', '$type', 'Polygon']],
    paint: {
      'fill-color': ['case', ['==', ['get', 'active'], 'true'], 'orange', 'blue'],
      'fill-opacity': 0.1,
    },
  },
  {
    id: 'gl-draw-lines',
    type: 'line',
    filter: ['any', ['==', '$type', 'LineString'], ['==', '$type', 'Polygon']],
    layout: { 'line-cap': 'round', 'line-join': 'round' },
    paint: {
      'line-color': ['case', ['==', ['get', 'active'], 'true'], 'orange', 'blue'],
      'line-dasharray': [
        'case',
        ['==', ['get', 'active'], 'true'],
        ['literal', [0.2, 2]],
        ['literal', [0.2, 2]],
      ],
      'line-width': 2,
    },
  },
  {
    id: 'gl-draw-point-outer',
    type: 'circle',
    filter: ['all', ['==', '$type', 'Point'], ['==', 'meta', 'feature']],
    paint: {
      'circle-radius': ['case', ['==', ['get', 'active'], 'true'], 7, 5],
      'circle-color': 'white',
    },
  },
  {
    id: 'gl-draw-point-inner',
    type: 'circle',
    filter: ['all', ['==', '$type', 'Point'], ['==', 'meta', 'feature']],
    paint: {
      'circle-radius': ['case', ['==', ['get', 'active'], 'true'], 5, 3],
      'circle-color': ['case', ['==', ['get', 'active'], 'true'], 'orange', 'blue'],
    },
  },
  {
    id: 'gl-draw-vertex-outer',
    type: 'circle',
    filter: [
      'all',
      ['==', '$type', 'Point'],
      ['==', 'meta', 'vertex'],
      ['!=', 'mode', 'simple_select'],
    ],
    paint: {
      'circle-radius': ['case', ['==', ['get', 'active'], 'true'], 7, 5],
      'circle-color': 'white',
    },
  },
  {
    id: 'gl-draw-vertex-inner',
    type: 'circle',
    filter: [
      'all',
      ['==', '$type', 'Point'],
      ['==', 'meta', 'vertex'],
      ['!=', 'mode', 'simple_select'],
    ],
    paint: {
      'circle-radius': ['case', ['==', ['get', 'active'], 'true'], 5, 3],
      'circle-color': 'orange',
    },
  },
  {
    id: 'gl-draw-midpoint',
    type: 'circle',
    filter: ['all', ['==', 'meta', 'midpoint']],
    paint: { 'circle-radius': 3, 'circle-color': 'orange' },
  },
];
